/* ==========================================================================
   <asd-product-card>

   Variant selection, price, colour driven media and quick add for the ASD
   product card.

   Not to be confused with assets/asd-product-card.css, which is an ASD skin
   over Horizon's own <product-card> used by the default collection template.
   This file drives the ASD card markup instead, and the two never meet.

   Why a custom element: the homepage grids bind each card once from their own
   section script, which a collection grid cannot do. The facet system
   re-renders the grid through the Section Rendering API and infinite scroll
   appends pages, so cards arrive long after any section script has run. The
   browser upgrades a custom element the moment it lands, however it got there.

   Why everything is read on demand: a section morph does not always replace
   the card. It frequently reuses this element and rewrites what is inside it,
   which means any node reference or parsed variant list cached at upgrade time
   can end up describing markup that is no longer on the page. So the listeners
   are delegated on the host - the one node a morph keeps - and every handler
   reads the DOM as it is at that moment.

   Plain HTMLElement rather than Component from @theme/component: the card is
   driven by data attributes and class names rather than refs and declarative
   events, and delegation already survives the re-render that refs exist to
   handle.
   ========================================================================== */

const ADD_PATH = 'cart/add.js';

const routeRoot = () => window.Shopify?.routes?.root || '/';

class ASDProductCard extends HTMLElement {
  // A private field, not an attribute. A morph strips attributes the incoming
  // markup does not carry, so an attribute guard gets silently cleared - and a
  // later reconnect would then bind a second set of listeners and double every
  // quick add. A field is invisible to morphing and dies with the element.
  #bound = false;
  #warmedFor = null;

  connectedCallback() {
    if (!this.#bound) {
      this.#bound = true;
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('change', this.#handleChange);
    }

    this.#warmColourImages();
  }

  /* ---------------------------------------------------------------- reading */

  get #variants() {
    const node = this.querySelector('[data-product-variants-json]');
    if (!node) return [];

    try {
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  get #colourIndex() {
    const group = this.querySelector('[data-color-option]');
    return group ? Number(group.dataset.optionGroupIndex) : -1;
  }

  // One value per option group, in group order, which is option order.
  #selectedOptions() {
    return [...this.querySelectorAll('[data-option-group-index]')].map((group) => {
      const selected = group.querySelector('.asd-pcard__size-btn.is-selected');
      if (selected) return selected.dataset.optionValue;

      const dropdown = group.querySelector('.asd-pcard__variant-select');
      return dropdown ? dropdown.value : undefined;
    });
  }

  /* --------------------------------------------------------------- handlers */

  #handleClick = (event) => {
    const addButton = event.target.closest('[data-add-to-cart-btn]');
    if (addButton) {
      event.preventDefault();
      this.#addToCart(addButton);
      return;
    }

    const option = event.target.closest('.asd-pcard__size-btn');
    if (!option) return;

    event.preventDefault();
    if (option.classList.contains('is-disabled')) return;

    for (const sibling of option.closest('.asd-pcard__size-swatches')?.querySelectorAll('.asd-pcard__size-btn') ?? []) {
      sibling.classList.remove('is-selected');
      sibling.setAttribute('aria-pressed', 'false');
    }
    option.classList.add('is-selected');
    option.setAttribute('aria-pressed', 'true');

    this.#updateVariant();
  };

  #handleChange = (event) => {
    if (event.target.closest('.asd-pcard__variant-select')) this.#updateVariant();
  };

  /* ---------------------------------------------------------------- updating */

  #updateVariant() {
    // Cheap, and it guarantees the data describes the markup currently on the
    // page rather than whatever was here before the last filter render.
    this.#warmColourImages();

    const variants = this.#variants;
    const selected = this.#selectedOptions();
    const matched = variants.find((variant) =>
      variant.options.every((option, index) => option === selected[index])
    );

    const quickButton = this.querySelector('[data-add-to-cart-btn]');

    if (matched) {
      if (quickButton) {
        quickButton.dataset.variantId = matched.id;
        quickButton.disabled = !matched.available;
      }
      this.#renderPrice(matched);
    } else if (quickButton) {
      // No such combination. The add button is the one thing that must not stay
      // actionable; the price keeps its last real figure, because blanking it
      // reads as broken.
      quickButton.disabled = true;
    }

    this.#applyMedia(matched, selected, variants);
  }

  // Built from nodes rather than innerHTML: these strings come from the money
  // filter, whose format is merchant configurable.
  #renderPrice(variant) {
    const priceElement = this.querySelector('[data-price-display]');
    if (!priceElement) return;

    priceElement.textContent = '';

    if (variant.formatted_compare_at_price) {
      const was = document.createElement('s');
      was.className = 'asd-pcard__price-was';
      was.textContent = variant.formatted_compare_at_price;
      priceElement.append(was);
    }

    const now = document.createElement('span');
    now.textContent = variant.formatted_price;
    priceElement.append(now);
  }

  // Resolved from the colour value rather than the matched variant, so the photo
  // still changes when the exact colour + size combination does not exist or is
  // sold out: a specific combination often has no image of its own while another
  // size in the same colour does.
  #applyMedia(matched, selected, variants) {
    const image = this.querySelector('.asd-pcard__media img');
    const colourIndex = this.#colourIndex;
    if (!image || colourIndex < 0) return;

    let source = matched?.image ? matched : null;

    if (!source) {
      const colourValue = selected[colourIndex];
      if (!colourValue) return;

      source = variants.find(
        (variant) => variant.options[colourIndex] === colourValue && variant.image
      );
    }
    if (!source) return;

    // Compared through a dataset marker because img.src reads back as an
    // absolute URL while image_url hands us a protocol relative one.
    if (image.dataset.asdAppliedImage === source.image) return;
    image.dataset.asdAppliedImage = source.image;

    if (source.image_srcset) image.srcset = source.image_srcset;
    else image.removeAttribute('srcset');

    image.src = source.image;
  }

  // Swapping an <img src> to a URL the browser has not fetched shows a beat of
  // blank on the first colour click and nothing after. Warming the distinct
  // variant images means even the first click swaps to a cached image. Keyed by
  // product, so a card the grid re-rendered into a different product warms again.
  #warmColourImages() {
    const productId = this.dataset.productCardId ?? '';
    if (this.#warmedFor === productId) return;
    this.#warmedFor = productId;

    if (this.#colourIndex < 0) return;

    const seen = new Set();
    for (const variant of this.#variants) {
      if (!variant.image || seen.has(variant.image)) continue;
      seen.add(variant.image);

      const warm = new Image();
      if (variant.image_srcset) warm.srcset = variant.image_srcset;
      warm.src = variant.image;
    }
  }

  /* ---------------------------------------------------------------- quick add */

  async #addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.disabled = true;

    try {
      const response = await fetch(routeRoot() + ADD_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });

      if (!response.ok) throw new Error('Add to cart failed');
      await response.json();

      // Posted straight to the Cart AJAX API, so the ASD drawer is told
      // directly rather than through the theme's product form.
      window.ASDCart?.notify({ open: true });
    } catch {
      button.classList.add('is-error');
      setTimeout(() => button.classList.remove('is-error'), 1200);
    } finally {
      button.disabled = false;
    }
  }
}

if (!customElements.get('asd-product-card')) {
  customElements.define('asd-product-card', ASDProductCard);
}
